import React, { ReactNode } from 'react';
import moment from 'moment';
import { Text } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import T from 'Components/Helpers/translate';

import './HistoryLogMonthHeader.scss';

type DateLike = Date | string | null;

/**
 * Groups a timeline feed into months, the way the Angular cards did with their
 * `timelineHeader` / `showHeader` pair (see `contactDashboard.js`): a header is
 * emitted whenever an entry falls in another month than the entry above it.
 */
export const getMonthKey = (date?: DateLike) => (date ? moment(date).format('YYYY-MM') : 'noDate');

export const getMonthLabel = (date?: DateLike) => (date ? moment(date).format('MMMM YYYY') : T('todo.noDate'));

const HistoryLogMonthHeader = ({ date }: { date?: DateLike }) => {
	const classes = new BemClass('HistoryLogMonthHeader');
	return (
		<div className={classes.b()}>
			<Text className={classes.elem('label').b()} color="grey-8">
				{getMonthLabel(date)}
			</Text>
		</div>
	);
};

/**
 * Interleaves month headers into an already date-sorted list of entries. The
 * feed has to stay sorted for the headers to read correctly — every history log
 * that uses this sorts on date in its fetcher.
 */
export const withMonthHeaders = <T extends { date?: DateLike }>(
	entries: T[],
	renderEntry: (entry: T, index: number) => ReactNode
): ReactNode[] => {
	const nodes: ReactNode[] = [];
	let previousKey: string | null = null;

	entries.forEach((entry, index) => {
		const key = getMonthKey(entry.date);
		if (key !== previousKey) {
			nodes.push(<HistoryLogMonthHeader key={`month-${key}`} date={entry.date} />);
			previousKey = key;
		}
		nodes.push(renderEntry(entry, index));
	});

	return nodes;
};

export default HistoryLogMonthHeader;
